import type { RouteDef } from '../../app/routes'
import type { WrittenPage } from '../sections'

/**
 * The "Top 5 AI Success Strategies" e-book's own page.
 *
 * Built to the design of the digitopia.co page it replaces
 * (`/impact-labs/ebooks/top-5-ai-success-strategies/`), sent as five screenshots
 * on 2026-08-28 with *"referans alarak ilgili sayfayı tastamam şekilde,
 * aynısını, tıpkısını tasarla"*. Band for band that page's: the two-column hero
 * with the lit-city artwork beside it, the grey "Why Download This Ebook?" card
 * with the pair of cover mockups and the three promises, the "What You'll Learn"
 * card with the five bolded lessons, and the centred "Download Your Free Ebook
 * Now!" ask. The copy is that page's copy, verbatim.
 *
 * Eighth e-book page after Transform Better, Agentic AI, Sustainability
 * Innovation, Measurement, AI in Fashion Retail, The Leader's Guide to
 * Generative AI and Transforming Pain into Power, and it follows
 * `ebook-transforming-pain-into-power.ts` in every place the reference and this
 * site disagree — the same departures, for the same reasons:
 *
 *  * **The eyebrow is the site's own**, over the copy column, rather than
 *    digitopia.co's small left-pinned "UNLOCK THE FULL POTENTIAL OF AI" label.
 *    Every other hero here is that shape, and the words are unchanged.
 *  * **The download is a direct link to the PDF, not a gated form.** The
 *    reference puts a nine-field HubSpot form between the reader and the file;
 *    no form on this site submits anywhere yet, so reproducing it would mean the
 *    page's only call to action silently drops every lead. The `check` below
 *    says so, as the sibling pages' do.
 *  * **"Join Our Community" is a Contact Us band instead**, asked for in the
 *    same message — *"join our community yerine contact us yeterli"*. The
 *    reference's band is a newsletter subscribe, and there is no newsletter here
 *    for it to subscribe anybody to; the section keeps its place in the page
 *    between the second card and the closing ask, and sends the reader to
 *    `/en/contact-us`, which exists.
 *
 * **The stack mockup is this site's addition.** The reference draws the "What
 * You'll Learn" card with an empty right half; the sibling e-book pages all put
 * the stacked-copies mockup there, and marketing sent it with the screenshots.
 *
 * **The slug is confirmed, and the title rule was right about it**: the
 * screenshots were captured from
 * `digitopia.co/impact-labs/ebooks/top-5-ai-success-strategies/` and carry that
 * address in their filenames.
 */
export const route: RouteDef = {
  id: 'ebookTop5AiSuccessStrategies',
  path: 'impact-labs/ebooks/top-5-ai-success-strategies',
  priority: 0.7,
}

/**
 * The PDF, in the `website-documents` bucket rather than in `public/` — the same
 * object `content/ebooks.ts` already lists for this e-book's card, spelled out
 * here because this page needs it on four buttons and that file's `pdf()` helper
 * is private to the catalogue. A page's own address and file are local constants
 * in its own module, not keys in `content/pages/links.ts`: that file is for CTAs
 * shared between pages, and two concurrent runs adding two e-books to it wrote
 * the same lines.
 */
const PDF
  = 'https://njdxtkezxgkrmwwhzcne.supabase.co/storage/v1/object/public/website-documents/ebook/'
  + '6b09bdf8-a5f9-432b-81c4-100ffd11e0d8-digitopia-report-top5ai-r2.pdf'

export const ebookTop5AiSuccessStrategies: WrittenPage = {
  navLabel: 'Top 5 AI Success Strategies',
  title: 'Top 5 AI Success Strategies',
  description:
    'Actionable insights and real-world case studies to help you harness the power of AI, elevate customer experiences, streamline operations, and drive innovation. Download the e-book from Digitopia Impact Labs.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Unlock the Full Potential of AI',
      headline: 'Top 5 AI Success Strategies',
      body: [
        'Ready to revolutionize your business with AI? Our comprehensive ebook offers actionable '
          + 'insights and real-world case studies to help you harness the power of AI. Learn how to '
          + 'elevate customer experiences, streamline operations, and drive innovation.',
      ],
      ctas: [{ label: 'Download', href: PDF }],
      photo: {
        src: '/photo/top5-ai-ebook-artwork.webp',
        alt:
          'The e-book’s cover artwork: a city at night seen from above, its streets and towers '
          + 'lit in blue and violet with points of light rising above them.',
      },
      // The artwork is square and the site's hero box is 3:2, which would take
      // the sky and the foreground off it.
      photoFrame: 'square',
      /*
        Clause 1 — THE DOWNLOAD IS NOT GATED — resolved 2026-08-30. It said no
        form on this site submits anywhere and the Send button is disabled on
        purpose. Both stopped being true today: the form posts to
        `website-form-lead`, the submission opens a task on its page's own
        counterpart, and every Download here carries `data-gated-download`, so
        the reader is asked for an email and the PDF is sent to it. No HubSpot
        form id is needed for any of it. What is left is clause 2.
      */
      check:
        'THE ARTWORK IS 768–900px WIDE. Those are the largest copies of the '
        + 'cover artwork and the two mockups anybody has sent; every other hero on '
        + 'the site is 2560px, so this one is painted at roughly its own width on a '
        + 'laptop with nothing left over for a retina screen. If bigger exports '
        + 'exist wherever the covers were made:\n'
        + '     node apps/website/scripts/attach.mjs <file> photo-src/top5-ai-ebook-artwork.webp\n'
        + '     pnpm --filter @digitopia/website images\n'
        + '   and nothing in this file changes.',
    },
    {
      kind: 'cta',
      tone: 'card',
      // Mockup left, offer right — the reference's order, and it alternates with
      // the card under it.
      flip: true,
      heading: 'Why Download This Ebook?',
      points: [
        'Practical Insights: Understand how to leverage AI for personalized customer '
          + 'interactions, optimized operations, and more.',
        'Real-World Examples: See how leading companies like Netflix, General Electric, and '
          + 'Google are using AI to stay ahead.',
        'Actionable Strategies: Get step-by-step guidance on building AI capabilities, adopting '
          + 'advanced technologies, and ensuring ethical AI deployment.',
      ],
      ctas: [{ label: 'Download the eBook', href: PDF }],
      photo: {
        src: '/photo/top5-ai-ebook-covers.webp',
        alt:
          'Two copies of Top 5 AI Success Strategies, one behind the other, on a white '
          + 'background.',
      },
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'What You’ll Learn',
      points: [
        'Elevate Customer Experiences: Use AI to personalize interactions and enhance '
          + 'satisfaction.',
        'Streamline Operations: Optimize processes with AI-driven automation and predictive '
          + 'maintenance.',
        'Empower Your Workforce: Develop AI skills and foster a culture of innovation.',
        'Adopt Advanced AI Technologies: Implement machine learning, NLP, and computer vision.',
        'Implement Effective AI Governance: Ensure responsible and ethical AI use.',
      ],
      ctas: [{ label: 'Download the eBook', href: PDF }],
      photo: {
        src: '/photo/top5-ai-ebook-stack.webp',
        alt: 'A stack of printed copies of Top 5 AI Success Strategies seen from above.',
      },
    },
    {
      // The reference's "Join Our Community" newsletter band, asked to be a
      // Contact Us instead — same place in the page, a destination that exists.
      kind: 'prose',
      heading: 'Contact Us',
      sub:
        'Have a question about putting these five strategies to work in your organization? Talk '
        + 'to us, and we will help you turn AI ambition into measurable outcomes.',
      cta: { label: 'Contact Us', href: '#contact' },
    },
    {
      kind: 'prose',
      align: 'center',
      heading: 'Download Your Free Ebook Now!',
      sub:
        'Transform your business and stay competitive with our expert insights and strategies. '
        + 'Don’t miss out!',
      cta: { label: 'Download the eBook', href: PDF },
    },
  ],
}
